import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import constant from "../../constant";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  Image,
  // Font, // Uncomment if you register fonts
} from "@react-pdf/renderer";

// Register fonts if needed
// Font.register({ family: 'Roboto', src: '...' });

// Tax Calculation Function - Robust version
const calculateTaxDetails = (items, isInterState) => {
  const taxDetails = {
    cgst: {},
    sgst: {},
    igst: {},
    totalTax: 0,
    grandTotal: 0,
    taxableTotal: 0,
  };
  // Ensure items is an array before iterating
  (items || []).forEach((item) => {
    const taxableAmount = parseFloat(item.itemAmount) || 0;
    const taxRate = parseFloat(item.taxRate) || 0;
    const totalAmountItem = parseFloat(item.totalAmount) || 0; // Use item's total amount for grand total sum
    let taxAmount, splitRate;

    taxDetails.taxableTotal += taxableAmount;

    if (isInterState) {
      taxAmount = (taxableAmount * taxRate) / 100;
      taxAmount = Math.round(taxAmount * 100) / 100;
      taxDetails.igst[taxRate] = (taxDetails.igst[taxRate] || 0) + taxAmount;
      taxDetails.totalTax += taxAmount;
    } else {
      splitRate = taxRate / 2;
      taxAmount = (taxableAmount * splitRate) / 100;
      taxAmount = Math.round(taxAmount * 100) / 100;
      taxDetails.cgst[splitRate] =
        Math.round(((taxDetails.cgst[splitRate] || 0) + taxAmount) * 100) / 100;
      taxDetails.sgst[splitRate] =
        Math.round(((taxDetails.sgst[splitRate] || 0) + taxAmount) * 100) / 100;
      taxDetails.totalTax += taxAmount * 2;
    }
    // Sum item totals for Grand Total - more reliable if item.totalAmount includes tax correctly
    taxDetails.grandTotal += totalAmountItem;
  });

  // Round final calculated totals
  taxDetails.taxableTotal = Math.round(taxDetails.taxableTotal * 100) / 100;
  taxDetails.totalTax = Math.round(taxDetails.totalTax * 100) / 100;
  // Round the summed grandTotal OR recalculate from taxable + tax if preferred
  taxDetails.grandTotal = Math.round(taxDetails.grandTotal * 100) / 100;
  // Example recalculation (if item.totalAmount is unreliable):
  // taxDetails.grandTotal = Math.round((taxDetails.taxableTotal + taxDetails.totalTax) * 100) / 100;

  return taxDetails;
};

// Create styles - ENHANCED for better PDF visuals
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF", // Explicit white background
    padding: 15, // Standard A4 padding
    fontFamily: "Helvetica", // Default font
    fontSize: 9,
    color: "#333", // Default text color
  },
  // Section 1: Header (Tax Invoice Title)
  header: {
    backgroundColor: "#f0f0f0", // Light Gray Background for Header
    paddingVertical: 8, // Vertical padding
    paddingHorizontal: 10, // Horizontal padding
    marginBottom: 15, // Space below header
    textAlign: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#cccccc",
    borderBottomStyle: "solid",
  },
  headerText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#111", // Darker color for title
    textTransform: "uppercase",
  },
  headerSubText: {
    fontSize: 6,
    fontWeight: "normal",
    color: "#555", // Slightly lighter subtext
    marginTop: 3,
  },
  // Section 2: Company Info & Invoice Details
  companyInfoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15, // Space below this section
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0", // Lighter border
    borderBottomStyle: "solid",
    paddingBottom: 10,
  },
  companyDetails: {
    // Container for Seller Info
    width: "70%", // Slightly more width
    paddingRight: 15,
  },
  invoiceDetails: {
    // Container for Invoice Specifics
    width: "30%",
    // Removed borderRight from left column style
  },
  companyName: {
    fontSize: 12, // Slightly larger company name
    fontWeight: "bold",
    marginBottom: 5,
    color: "#000000",
  },
  addressText: {
    fontSize: 9,
    marginBottom: 3,
    lineHeight: 1.4, // Improved line spacing
    color: "#444",
  },
  // Styles for Invoice Details Box (Right Side of Section 2)
  invoiceDetailsBox: {
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 3,
    padding: 8,
    backgroundColor: "#f9f9f9", // Very light background for this box
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  infoLabel: {
    fontSize: 9,
    color: "#555", // Label color
  },
  infoValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#111", // Value color
    textAlign: "right",
  },
  // Section 3: Billing & Shipping Addresses
  addressSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15, // Space below addresses
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0", // Lighter border
    borderBottomStyle: "solid",
    paddingBottom: 10,
  },
  addressColumn: {
    // Common style for Bill To / Ship To
    width: "48%", // Slightly less than 50% for spacing
  },
  sectionTitle: {
    // For "Bill To", "Ship To", "Terms", etc.
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 6,
    color: "#000",
    textTransform: "uppercase",
    // borderBottomWidth: 1,
    // borderBottomColor: '#e0e0e0',
    paddingBottom: 3,
  },
  addressPartyName: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#111",
    marginBottom: 4,
  },
  // Section 4: Items Table
  tableContainer: {
    marginTop: 5,
    marginBottom: 15, // Space below table
    borderWidth: 1,
    borderColor: "#cccccc", // Border around the whole table
    borderRadius: 3, // Slightly rounded corners
    overflow: "hidden", // Ensures border radius clips content
  },
  table: {
    display: "table",
    width: "auto",
  },
  tableHeader: {
    backgroundColor: "#f0f0f0", // Header background matches main header
    borderBottomWidth: 1,
    borderBottomColor: "#cccccc", // Stronger border for header row
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e8e8e8", // Light row separator
    borderBottomStyle: "solid",
    backgroundColor: "#fff", // Ensure rows are white by default
    minHeight: 20, // Minimum row height
    alignItems: "center", // Vertically center content in rows
  },
  tableRowEven: {
    backgroundColor: "#f9f9f9", // Background for even rows
  },
  uppercase: {
    textTransform: "uppercase",
  },
  // Cell Styles
  tableCellHeader: {
    paddingVertical: 6, // Slightly more padding for header
    paddingHorizontal: 5,
    fontSize: 8.5, // Slightly larger header font
    fontWeight: "bold",
    textAlign: "center",
    color: "#333",
    borderRightWidth: 1, // Vertical separators for header
    borderRightColor: "#cccccc",
    borderRightStyle: "solid",
  },
  tableCell: {
    paddingVertical: 5, // Padding for regular cells
    paddingHorizontal: 5,
    fontSize: 8,
    textAlign: "left",
    borderRightWidth: 1, // Vertical separators for cells
    borderRightColor: "#e8e8e8", // Lighter separators
    borderRightStyle: "solid",
    overflowWrap: "break-word", // Helps break long words
  },
  // Remove last border
  tableCellLast: { borderRightWidth: 0 },
  tableCellHeaderLast: { borderRightWidth: 0 },
  // Column Widths (Ensure they sum to 100% or use 'auto')
  tableCellNumber: { width: "5%" },
  tableCellItem: { width: "20%" }, // Slightly more for item/desc
  tableCellQty: { width: "10%" },
  tableCellRate: { width: "10%" },
  tableCellDiscount: { width: "8%" },
  tableCellTaxable: { width: "13%" },
  tableCellTaxPercent: { width: "8%" },
  tableCellTaxAmount: { width: "13%" },
  tableCellAmount: { width: "13%" }, // Slightly more for total

  // Text Alignments within cells
  textRight: { textAlign: "right" },
  textCenter: { textAlign: "center" },
  textLeft: { textAlign: "left" },

  // Section 5: Terms, Notes, Amount in Words & Totals
  summarySection: {
    // Combined section for terms and totals
    flexDirection: "row",
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#cccccc",
    borderTopStyle: "solid",
    paddingTop: 10,
  },
  summaryLeftColumn: {
    // Terms, Notes, Words
    width: "60%",
    paddingRight: 15,
  },
  summaryRightColumn: {
    // Totals Breakup
    width: "40%",
    paddingLeft: 15,
  },
  termsNotesText: {
    fontSize: 8.5,
    color: "#555",
    lineHeight: 1.4,
    marginBottom: 10, // Space between terms/notes if both exist
  },
  amountInWordsContainer: {
    marginTop: 10,
    paddingTop: 5,
  },
  amountInWordsLabel: {
    fontSize: 9,
    color: "#444",
    marginBottom: 3,
  },
  amountInWords: {
    fontSize: 9,
    fontWeight: "medium",
    fontStyle: "italic",
    color: "#000",
    lineHeight: 1.4,
  },
  // Totals Breakup Styling
  totalsBox: {
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 3,
    padding: 10,
    backgroundColor: "#f9f9f9", // Light background for totals box
  },
  amountRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    paddingVertical: 1.5, // Add small vertical padding
  },
  amountLabel: {
    fontSize: 9,
    color: "#444",
  },
  amountValue: {
    fontSize: 9,
    textAlign: "right",
    fontWeight: "normal",
    color: "#000",
  },
  divider: {
    // Dashed divider within totals
    borderBottomWidth: 1,
    borderBottomColor: "#dddddd",
    borderBottomStyle: "dashed",
    marginVertical: 5,
  },
  totalAmountRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
    paddingTop: 5,
    borderTopWidth: 1, // Solid border above Grand Total
    borderTopColor: "#aaaaaa",
    borderTopStyle: "solid",
  },
  totalAmountLabel: {
    fontSize: 10,
    fontWeight: "semibold",
    color: "#000",
  },
  totalAmountValue: {
    fontSize: 10,
    fontWeight: "semibold",
    textAlign: "right",
    color: "#000",
  },
  balanceDueValue: {
    fontWeight: "bold",
    color: "#c62828",
    fontSize: 9,
    textAlign: "right",
  },
  // Section 6: Footer (Bank & Signature)
  footerSection: {
    marginTop: 20,
    borderTopWidth: 0,
    borderTopColor: "#cccccc",
    borderTopStyle: "solid",
    paddingTop: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    // position: "absolute",
    // bottom: 30,
    // left: 30,
    // right: 30,
  },
  bankDetails: {
    width: "55%",
    paddingRight: 15,
  },
  bankDetailsTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 5,
    textTransform: "uppercase",
    color: "#111",
  },
  bankDetailsText: {
    fontSize: 8.5, // Slightly larger bank details font
    marginBottom: 3,
    color: "#333",
    lineHeight: 1.3,
  },
  signature: {
    // Container for signature area
    width: "40%",
    paddingLeft: 15,
    flexDirection: "column",
    justifyContent: "space-between", // Pushes signature line down
    alignItems: "flex-end", // Aligns content (For text, signature line) to right
    minHeight: 50, // Ensure minimum space for signature
  },
  signatureFor: {
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "center", // Center 'For Company Name'
    marginBottom: 5, // Increased Space for actual signature
    alignSelf: "center", // Center within the signature column
  },
  signatureLine: {
    // Just the line for signing
    fontSize: 9,
    textAlign: "center",
    borderTopWidth: 1,
    borderTopColor: "#aaaaaa",
    borderTopStyle: "solid",
    paddingTop: 5,
    width: "100%", // Line spans the signature column width
    color: "#333",
    marginTop: "auto", // Push to bottom if using flex space-between
  },
  pageNumber: {
    // Optional Page Numbering
    position: "absolute",
    fontSize: 8,
    bottom: 15,
    left: 0,
    right: 30, // Align to right, considering padding
    textAlign: "center",
    color: "grey",
  },
});

// Format date function - KEEP AS IS
const formatDate = (dateString) => {
  // ... (implementation remains the same)
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return dateString;
  }
};

// Number to words function - KEEP AS IS
const numberToWords = (num) => {
  // ... (implementation remains the same)
  if (typeof num !== "number" || isNaN(num)) {
    return "Invalid Number";
  }
  const units = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const scales = ["", "Thousand", "Lakh", "Crore"];
  const convertLessThanOneThousand = (n) => {
    /* ... */ if (n === 0) return "";
    if (n < 10) return units[n];
    if (n < 20) return teens[n - 10];
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + units[n % 10] : "")
      );
    return (
      units[Math.floor(n / 100)] +
      " Hundred" +
      (n % 100 !== 0 ? " and " + convertLessThanOneThousand(n % 100) : "")
    );
  };
  if (num === 0) return "Zero Rupees Only";
  const inrValue = Math.floor(num);
  const paiseValue = Math.round((num - inrValue) * 100);
  let words = "";
  let remaining = inrValue;
  let scaleIndex = 0;
  if (remaining > 0) {
    const firstSegment = remaining % 1000;
    if (firstSegment > 0) {
      words = convertLessThanOneThousand(firstSegment);
    }
    remaining = Math.floor(remaining / 1000);
    scaleIndex++;
    while (remaining > 0) {
      const segment = remaining % 100;
      if (segment > 0) {
        words =
          convertLessThanOneThousand(segment) +
          " " +
          scales[scaleIndex] +
          (words ? " " + words : "");
      }
      remaining = Math.floor(remaining / 100);
      scaleIndex++;
      if (scaleIndex >= scales.length) break;
    }
    words += " Rupees";
  }
  if (paiseValue > 0) {
    if (words) {
      words += " and ";
    }
    words += convertLessThanOneThousand(paiseValue) + " Paise";
  }
  if (!words) {
    return "Zero Rupees Only";
  }
  return words.charAt(0).toUpperCase() + words.slice(1) + " Only";
};

const InvoicePDF = ({
  invoiceData,
  taxDetailsCalculated,
  isInterStateInvoice,
}) => {
  async function getBase64FromUrl(url) {
    const response = await fetch(url, {
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",

      }
    });
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  const [base64Signature, setBase64Signature] = useState("");
  const [base64Logo, setBase64Logo] = useState("");

  useEffect(() => {
    async function fetchLogoSinature() {
      const base64Signature = await getBase64FromUrl(invoiceData.signature);
      const base64Logo = await getBase64FromUrl(invoiceData.logo);
      setBase64Signature(base64Signature);
      setBase64Logo(base64Logo);
    }
    fetchLogoSinature();
  }, []);

  if (!invoiceData || !invoiceData.salesData || !taxDetailsCalculated) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Error: Invoice data is incomplete or missing.</Text>
        </Page>
      </Document>
    );
  }

  // --- Calculations for PDF ---
  let totalMrpValue = 0;
  let totalDiscountValue = 0;
  const items = Array.isArray(invoiceData.salesData.items)
    ? invoiceData.salesData.items
    : [];

  items.forEach((item) => {
    const price = parseFloat(item.price) || 0;
    const quantity = parseFloat(item.quantity) || 0;
    const discount = parseFloat(item.discount) || 0;
    const itemMrp = price * quantity;
    let itemDiscount = 0;
    totalMrpValue += itemMrp;
    if (item.discountType === "%") {
      itemDiscount = (itemMrp * discount) / 100;
    } else {
      itemDiscount = discount * quantity;
    } // Assuming discount is per unit if not %
    totalDiscountValue += itemDiscount;
  });
  totalMrpValue = Number(totalMrpValue) || 0;
  totalDiscountValue = Number(totalDiscountValue) || 0;

  const totalAmount = parseFloat(invoiceData.salesData.totalAmount) || 0;
  const totalAmountRoundoff = Math.round(totalAmount);
  const amountReceived = parseFloat(invoiceData.salesData.amountReceived) || 0;
  const balanceAmount = totalAmountRoundoff - amountReceived;
  const roundOffValue = totalAmountRoundoff - totalAmount;

  // --- PDF Structure ---
  return (
    <Document title={`Invoice ${invoiceData.salesData.invoiceNumber}`}>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}></View>
          {invoiceData.isGstExempted ? (
            <Text style={styles.headerText}>Bill of Supply</Text>
          ) : (
            <Text style={styles.headerText}>TAX INVOICE</Text>
          )}
          <Text style={styles.headerSubText}>ORIGINAL FOR RECIPIENT</Text>
        </View>

        {/* Company Info & Invoice Details */}
        <View style={styles.companyInfoSection}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Image src={base64Logo} style={{ height: 60, width: 60, marginRight: 15 }} />
            <View>
              <Text style={styles.companyName}>
                {invoiceData.companyName || "N/A"}
              </Text>
              <Text style={styles.addressText}>
                {invoiceData.address?.streetAddress || "N/A"} {"\n"}
                {invoiceData.address?.city || "N/A"},{" "}
                {invoiceData.address?.state || "N/A"}{" "}
                {invoiceData.address?.pincode || ""} {"\n"}
                {!invoiceData.isGstExempted && invoiceData.gst && (
                  <Text style={styles.uppercase}>
                    GSTIN: {invoiceData.gst} {"\n"}
                  </Text>
                )}
                {invoiceData.mobile && (
                  <Text>
                    Contact: {invoiceData.mobile} {"\n"}
                  </Text>
                )}
              </Text>
            </View>
          </View>
          <View style={styles.invoiceDetails}>
            <View style={styles.invoiceDetailsBox}>
              {" "}
              {/* Added Box */}
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Invoice No.:</Text>
                <Text style={styles.infoValue}>
                  {invoiceData.salesData.invoiceNumber || "N/A"}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Invoice Date:</Text>
                <Text style={styles.infoValue}>
                  {formatDate(invoiceData.salesData.invoiceDate)}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Due Date:</Text>
                <Text style={styles.infoValue}>
                  {formatDate(invoiceData.salesData.dueDate)}
                </Text>
              </View>
              {invoiceData.salesData.poNumber && (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>P.O. Number:</Text>
                  <Text style={styles.infoValue}>
                    {invoiceData.salesData.poNumber}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Billing & Shipping Addresses */}
        <View style={styles.addressSection}>
          <View style={styles.addressColumn}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <Text style={styles.addressPartyName}>
              {invoiceData.salesData.partyData?.billingAddress?.name || "N/A"}
            </Text>
            <Text style={styles.addressText}>
              {invoiceData.salesData.partyData?.billingAddress?.streetAddress ||
                "N/A"}{" "}
              {"\n"}
              {invoiceData.salesData.partyData?.billingAddress?.city ||
                "N/A"},{" "}
              {invoiceData.salesData.partyData?.billingAddress?.state || "N/A"}{" "}
              {invoiceData.salesData.partyData?.billingAddress?.pincode || ""}{" "}
              {"\n"}
              {/* <Text>
                State Code:{" "}
                {invoiceData.salesData.partyData?.billingAddress?.stateCode ||
                  "N/A"}{" "}
                {"\n"}
              </Text> */}
              {!invoiceData.isGstExempted &&
                invoiceData.salesData.partyData?.billingAddress?.gstNo && (
                  <Text style={styles.uppercase}>
                    GSTIN:{" "}
                    {invoiceData.salesData.partyData.billingAddress.gstNo}{" "}
                    {"\n"}
                  </Text>
                )}
              {invoiceData.salesData.partyData?.panNumber && (
                <Text>
                  PAN: {invoiceData.salesData.partyData.panNumber} {"\n"}
                </Text>
              )}
              {invoiceData.salesData.partyData?.mobile && (
                <Text>Contact: {invoiceData.salesData.partyData.mobile}</Text>
              )}
            </Text>
          </View>
          <View style={styles.addressColumn}>
            <Text style={styles.sectionTitle}>Ship To</Text>
            <Text style={styles.addressPartyName}>
              {invoiceData.salesData.partyData?.shippingAddress?.name || "N/A"}
            </Text>
            <Text style={styles.addressText}>
              {invoiceData.salesData.partyData?.shippingAddress
                ?.streetAddress || "N/A"}{" "}
              {"\n"}
              {invoiceData.salesData.partyData?.shippingAddress?.city ||
                "N/A"},{" "}
              {invoiceData.salesData.partyData?.shippingAddress?.state || "N/A"}{" "}
              {invoiceData.salesData.partyData?.shippingAddress?.pincode || ""}{" "}
              {"\n"}
              {/* <Text>
                State Code:{" "}
                {invoiceData.salesData.partyData?.shippingAddress?.stateCode ||
                  "N/A"}{" "}
                {"\n"}
              </Text> */}
              {!invoiceData.isGstExempted &&
                invoiceData.salesData.partyData?.shippingAddress?.gstNo && (
                  <Text style={styles.uppercase}>
                    GSTIN:{" "}
                    {invoiceData.salesData.partyData.shippingAddress.gstNo}{" "}
                    {"\n"}
                  </Text>
                )}
              {invoiceData.salesData.partyData?.phoneNumber && (
                <Text>
                  Contact: {invoiceData.salesData.partyData.phoneNumber}
                </Text>
              )}
            </Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.tableContainer}>
          <View style={styles.table}>
            {/* Table Header */}
            <View style={[styles.tableRow, styles.tableHeader]} fixed>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellNumber,
                  styles.textCenter,
                ]}
              >
                #
              </Text>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellItem,
                  styles.textCenter,
                ]}
              >
                Item & Desc
              </Text>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellQty,
                  styles.textCenter,
                ]}
              >
                Qty
              </Text>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellQty,
                  styles.textCenter,
                ]}
              >
                HSN/SCN
              </Text>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellRate,
                  styles.textCenter,
                ]}
              >
                Rate (Rs)
              </Text>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellTaxable,
                  styles.textCenter,
                ]}
              >
                Taxable (Rs)
              </Text>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellTaxPercent,
                  styles.textCenter,
                ]}
              >
                Tax %
              </Text>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellTaxAmount,
                  styles.textCenter,
                ]}
              >
                Tax Amt (Rs)
              </Text>
              <Text
                style={[
                  styles.tableCellHeader,
                  styles.tableCellAmount,
                  styles.textCenter,
                  styles.tableCellHeaderLast,
                ]}
              >
                Total (Rs)
              </Text>
            </View>
            {/* Table Body */}
            {items.map((item, index) => {
              const itemAmount = parseFloat(item.itemAmount) || 0;
              const taxAmount = parseFloat(item.taxAmount) || 0;
              const totalAmountItem = parseFloat(item.totalAmount) || 0;
              const rate = parseFloat(item.price) || 0;
              const discount = parseFloat(item.discount) || 0;
              const quantity = parseFloat(item.quantity) || 0;
              const isLastItem = index === items.length - 1;
              return (
                <View
                  key={`item-${index}`}
                  style={[
                    styles.tableRow,
                    index % 2 !== 0 ? styles.tableRowEven : {},
                    isLastItem ? { borderBottomWidth: 0 } : {},
                  ]}
                  wrap={false}
                >
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellNumber,
                      styles.textCenter,
                    ]}
                  >
                    {index + 1}
                  </Text>
                  <View
                    style={[
                      styles.tableCell,
                      styles.tableCellItem,
                      styles.textCenter,
                    ]}
                  >
                    <Text style={{ fontWeight: "bold" }}>
                      {item.itemName || "-"}
                    </Text>
                    {/* {item.hsnCode && <Text style={{ fontSize: 7 }}> (HSN: {item.hsnCode})</Text>} */}
                    {item.description && (
                      <Text
                        style={{ fontSize: 7, marginTop: 2, color: "#555" }}
                      >
                        {item.description}
                      </Text>
                    )}
                  </View>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellQty,
                      styles.textCenter,
                    ]}
                  >
                    {quantity} {item.unit || "PCS"}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellQty,
                      styles.textCenter,
                    ]}
                  >
                    {" "}
                    {item.hsnCode}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellRate,
                      styles.textCenter,
                    ]}
                  >
                    {rate.toFixed(2)}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellTaxable,
                      styles.textCenter,
                    ]}
                  >
                    {itemAmount.toFixed(2)}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellTaxPercent,
                      styles.textCenter,
                    ]}
                  >
                    {item.taxRate || 0}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellTaxAmount,
                      styles.textCenter,
                    ]}
                  >
                    {taxAmount.toFixed(2)}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellAmount,
                      styles.textCenter,
                      styles.tableCellLast,
                    ]}
                  >
                    {totalAmountItem.toFixed(2)}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* Summary Section: Terms, Notes, Words & Totals */}
        <View style={styles.summarySection}>
          {/* Left: Terms, Notes, Amount in Words */}
          <View style={styles.summaryLeftColumn}>
            {invoiceData.salesData.terms && (
              <>
                <Text style={styles.sectionTitle}>Terms & Conditions</Text>
                <Text style={styles.termsNotesText}>
                  {invoiceData.salesData.terms}
                </Text>
              </>
            )}
            {invoiceData.salesData.notes && (
              <View
                style={invoiceData.salesData.terms ? { marginTop: 10 } : {}}
              >
                <Text style={styles.sectionTitle}>Notes</Text>
                <Text style={styles.termsNotesText}>
                  {invoiceData.salesData.notes}
                </Text>
              </View>
            )}
            {/* <View style={styles.amountInWordsContainer}>
              <Text style={styles.amountInWordsLabel}>Amount in Words:</Text>
              <Text style={styles.amountInWords}>{numberToWords(totalAmountRoundoff)}</Text>
            </View> */}
          </View>

          {/* Right: Calculation Summary */}
          <View style={styles.summaryRightColumn}>
            <View style={styles.totalsBox}>
              {" "}
              {/* Added Box */}
              <View style={styles.amountRow}>
                <Text style={styles.amountLabel}>MRP Total:</Text>
                <Text style={styles.amountValue}>
                  Rs {totalMrpValue.toFixed(2)}
                </Text>
              </View>
              {totalDiscountValue > 0 && (
                <View style={styles.amountRow}>
                  <Text style={styles.amountLabel}>Discount:</Text>
                  <Text style={styles.amountValue}>
                    (-) Rs {totalDiscountValue.toFixed(2)}
                  </Text>
                </View>
              )}
              <View style={styles.amountRow}>
                <Text style={styles.amountLabel}>Taxable Amount:</Text>
                <Text style={styles.amountValue}>
                  Rs {taxDetailsCalculated.taxableTotal.toFixed(2)}
                </Text>
              </View>
              <View style={styles.divider} /> {/* Divider before taxes */}
              {Object.entries(taxDetailsCalculated.igst).map(([rate, amount]) =>
                amount > 0 ? (
                  <View key={`igst-${rate}`} style={styles.amountRow}>
                    <Text style={styles.amountLabel}>IGST @{rate}%:</Text>
                    <Text style={styles.amountValue}>
                      Rs {amount.toFixed(2)}
                    </Text>
                  </View>
                ) : null
              )}
              {Object.entries(taxDetailsCalculated.cgst).map(([rate, amount]) =>
                amount > 0 ? (
                  <View key={`cgst-${rate}`} style={styles.amountRow}>
                    <Text style={styles.amountLabel}>CGST @{rate}%:</Text>
                    <Text style={styles.amountValue}>
                      Rs {amount.toFixed(2)}
                    </Text>
                  </View>
                ) : null
              )}
              {Object.entries(taxDetailsCalculated.sgst).map(([rate, amount]) =>
                amount > 0 ? (
                  <View key={`sgst-${rate}`} style={styles.amountRow}>
                    <Text style={styles.amountLabel}>SGST @{rate}%:</Text>
                    <Text style={styles.amountValue}>
                      Rs {amount.toFixed(2)}
                    </Text>
                  </View>
                ) : null
              )}
              {taxDetailsCalculated.totalTax > 0 && (
                <View style={styles.amountRow}>
                  <Text style={styles.amountLabel}>Total Tax:</Text>
                  <Text style={styles.amountValue}>
                    Rs {taxDetailsCalculated.totalTax.toFixed(2)}
                  </Text>
                </View>
              )}
              {Math.abs(roundOffValue) > 0.005 && (
                <View style={styles.amountRow}>
                  <Text style={styles.amountLabel}>Round Off:</Text>
                  <Text style={styles.amountValue}>
                    Rs {roundOffValue.toFixed(2)}
                  </Text>
                </View>
              )}
              <View style={styles.totalAmountRow}>
                <Text style={styles.totalAmountLabel}>Grand Total:</Text>
                <Text style={styles.totalAmountValue}>
                  Rs {totalAmountRoundoff.toFixed(2)}
                </Text>
              </View>
              <View style={styles.amountInWordsContainer}>
                {/* <Text style={styles.amountInWordsLabel}>Amount in Words:</Text> */}
                <Text style={styles.amountInWords}>
                  {numberToWords(totalAmountRoundoff)}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Footer Section */}
        <View style={styles.footerSection}>
          <View style={styles.bankDetails}>
            <Text style={styles.bankDetailsTitle}>Bank Details</Text>
            <Text style={styles.bankDetailsText}>
              Bank Name: {invoiceData.bankDetails?.bankName || "-"} {"\n"}
              A/C Name: {invoiceData.bankDetails?.accountName || "-"} {"\n"}
              A/C No: {invoiceData.bankDetails?.accountNumber || "-"} {"\n"}
              IFSC: {invoiceData.bankDetails?.ifsc || "-"} {"\n"}
            </Text>
          </View>
          <View style={styles.signature}>
            <Text style={styles.signatureFor}>
              For {invoiceData.companyName || "Your Company"}
            </Text>
            <Image
              style={{
                height: 50,
                width: 100,
                marginLeft: "auto",
                marginRight: "auto",
              }}
              src={base64Signature}
            />
            <Text style={styles.signatureLine}>Authorised Signatory</Text>
          </View>
        </View>

        {/* Optional: Page Number */}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

// Main Invoice Component - Includes HTML Preview
const Invoice = () => {
  const { id } = useParams();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taxDetailsCalculated, setTaxDetailsCalculated] = useState({
    cgst: {},
    sgst: {},
    igst: {},
    totalTax: 0,
    grandTotal: 0,
    taxableTotal: 0,
  });
  const [isInterStateInvoice, setIsInterStateInvoice] = useState(false);

  const constructApiUrl = (endpoint) => {
    const userRole = localStorage.getItem("role");
    const adminSelectedUser = localStorage.getItem("adminSelectedUser");
    let apiUrl = `${constant.REACT_APP_API_URL}${endpoint}`;
    if (userRole === "admin" && adminSelectedUser) {
      apiUrl = `${constant.REACT_APP_API_URL}${endpoint}${endpoint.includes("?") ? "&" : "?"
        }userId=${adminSelectedUser}`;
    }
    return apiUrl;
  };

  // Fetch Data useEffect - Robust
  useEffect(() => {
    const fetchInvoiceData = async () => {
      setLoading(true);
      setInvoiceData(null); // Reset data on new fetch
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("No authentication token found");
          setLoading(false);
          return;
        }

        const apiUrl = constructApiUrl(`/sales/${id}`);
        // `${constant.REACT_APP_API_URL}/sales/${id}`
        const response = await fetch(
          apiUrl,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          const result = await response
            .json()
            .catch(() => ({
              message: `HTTP error! status: ${response.status}`,
            }));
          throw new Error(
            result.message || `HTTP error! status: ${response.status}`
          );
        }
        const result = await response.json();
        if (result.success && result.data) {
          setInvoiceData(result.data);
        } else {
          console.error(
            "API Error:",
            result.message || "Failed to fetch invoice data or data is missing"
          );
        }
      } catch (error) {
        console.error("Fetch Invoice Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoiceData();
  }, [id]);

  // Calculate Tax useEffect - Robust
  useEffect(() => {
    if (
      invoiceData?.salesData?.items &&
      invoiceData?.gst &&
      invoiceData.salesData.partyData?.billingAddress
    ) {
      const companyGstPrefix = invoiceData.gst.substring(0, 2).toUpperCase();
      const partyGst = invoiceData.salesData.partyData.billingAddress.gstNo;
      let isInterState = null;
      if (!partyGst) {
        isInterState = (invoiceData.address?.state.toLowerCase() !== invoiceData.salesData?.partyData.billingAddress?.state.toLowerCase())
      } else {
        const partyGstPrefix = partyGst
          ? partyGst.substring(0, 2).toUpperCase()
          : null;
        isInterState = !!(
          companyGstPrefix &&
          partyGstPrefix &&
          companyGstPrefix !== partyGstPrefix
        );

      }
      setIsInterStateInvoice(isInterState);
      const itemsForTax = (invoiceData.salesData.items || []).map((item) => ({
        ...item,
      }));
      const calculatedTax = calculateTaxDetails(itemsForTax, isInterState);
      setTaxDetailsCalculated(calculatedTax);
      // console.log("Calculated Tax Details:", calculatedTax);
      // console.log(invoiceData);
    } else {
      setTaxDetailsCalculated({
        cgst: {},
        sgst: {},
        igst: {},
        totalTax: 0,
        grandTotal: 0,
        taxableTotal: 0,
      });
      setIsInterStateInvoice(false);
      //  setIsInterStateInvoice(invoiceData.address?.state.toLowerCase() !== invoiceData.salesData?.partyData.billingAddress?.state.toLowerCase())
      if (invoiceData) {
        console.warn(
          "Could not calculate taxes due to missing data (Seller GST, Items, Billing Address)."
        );
      }
    }
  }, [invoiceData]);

  // --- Helper Calculations for HTML Preview ---
  let previewItemsTotal = taxDetailsCalculated.taxableTotal;
  let previewTotalDiscount = 0;
  let previewTotalMrp = 0;
  let previewTotalAmount = 0;
  let previewTotalAmountRoundoff = 0;
  let previewAmountReceived = 0;
  let previewBalanceAmount = 0;
  let previewRoundOffValue = 0;
  if (invoiceData?.salesData?.items) {
    (invoiceData.salesData.items || []).forEach((item) => {
      /* ... calculate previewTotalMrp, previewTotalDiscount ... */
      const price = parseFloat(item.price) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const discount = parseFloat(item.discount) || 0;
      const itemMrp = price * quantity;
      let itemDiscount = 0;
      previewTotalMrp += itemMrp;
      if (item.discountType === "%") {
        itemDiscount = (itemMrp * discount) / 100;
      } else {
        itemDiscount = discount * quantity;
      }
      previewTotalDiscount += itemDiscount;
    });
    previewTotalAmount = parseFloat(invoiceData.salesData.totalAmount) || 0;
    previewTotalAmountRoundoff = Math.round(previewTotalAmount);
    previewAmountReceived =
      parseFloat(invoiceData.salesData.amountReceived) || 0;
    previewBalanceAmount = previewTotalAmountRoundoff - previewAmountReceived;
    previewRoundOffValue = previewTotalAmountRoundoff - previewTotalAmount;
  }
  previewTotalMrp = Number(previewTotalMrp) || 0;
  previewTotalDiscount = Number(previewTotalDiscount) || 0;
  previewTotalAmount = Number(previewTotalAmount) || 0;
  previewTotalAmountRoundoff = Number(previewTotalAmountRoundoff) || 0;
  previewAmountReceived = Number(previewAmountReceived) || 0;
  previewBalanceAmount = Number(previewBalanceAmount) || 0;
  previewRoundOffValue = Number(previewRoundOffValue) || 0;

  // --- Loading State ---
  if (loading) {
    return (
      /* ... Loading Spinner ... */
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-t-4 border-blue-500 border-solid rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading invoice data...</p>
        </div>
      </div>
    );
  }

  // --- Error or No Data State ---
  if (!invoiceData) {
    return (
      /* ... Error Message ... */
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 text-red-500 mx-auto mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-xl font-semibold text-gray-700">
            Invoice Not Found or Error Loading
          </div>
          <p className="text-gray-500 mt-2 text-sm">
            The requested invoice (ID: {id}) could not be retrieved or is
            incomplete.
          </p>
        </div>
      </div>
    );
  }

  // --- Render Invoice View with HTML Preview ---
  return (
    <div className="container mx-auto p-4 pt-20 md:pt-4 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <h1 className="text-2xl font-bold text-gray-800">
            Invoice #{invoiceData.salesData.invoiceNumber || "N/A"}
          </h1>
          <div className="flex gap-3">
            {/* Download Button Only */}
            <PDFDownloadLink
              document={
                <InvoicePDF
                  invoiceData={invoiceData}
                  taxDetailsCalculated={taxDetailsCalculated}
                  isInterStateInvoice={isInterStateInvoice}
                />
              }
              fileName={`Invoice-${invoiceData.salesData.invoiceNumber || "download"
                }.pdf`}
              className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors shadow hover:shadow-md"
            >
              {({ loading: downloadLoading, error: downloadError }) => {
                if (downloadError) {
                  console.error("PDF Download Link Error:", downloadError);
                  return (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-red-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Error</span>
                    </>
                  );
                }
                return (
                  <>
                    {downloadLoading ? (
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                    )}
                    <span>
                      {downloadLoading ? "Generating..." : "Download"}
                    </span>
                  </>
                );
              }}
            </PDFDownloadLink>
          </div>
        </div>
        {/* Status and Dates Bar */}
        <div className="bg-gray-100 rounded-lg shadow-sm p-3 mb-6 flex justify-between items-center flex-wrap gap-3 text-sm border border-gray-200">
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 flex-shrink-0 ${previewBalanceAmount <= 0 ? "bg-green-500" : "bg-yellow-500"
                }`}
            ></div>
            <span className="font-medium text-gray-700">
              {previewBalanceAmount <= 0
                ? "Status: Paid"
                : `Status: Pending (Rs ${previewBalanceAmount.toFixed(2)})`}
            </span>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center">
              <span className="text-gray-600 mr-1">Issued:</span>
              <span className="font-medium text-gray-800">
                {formatDate(invoiceData.salesData.invoiceDate)}
              </span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-600 mr-1">Due:</span>
              <span className="font-medium text-gray-800">
                {formatDate(invoiceData.salesData.dueDate)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* --- DETAILED HTML PREVIEW --- */}
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200 print:shadow-none print:border-none mb-10">
        <div className="bg-gray-100 text-gray-800 py-3 px-6 text-center font-semibold border-b border-gray-300">
          {invoiceData.isGstExempted ? (
            <h2 className="text-lg">Bill of Supply</h2>
          ) : (
            <h2 className="text-lg">TAX INVOICE</h2>
          )}
          <p className="text-xs">ORIGINAL FOR RECIPIENT</p>
        </div>
        <div className="p-4 md:p-6">
          {/* Company Info & Invoice Details */}
          <div className="flex flex-col md:flex-row border-b border-gray-200 pb-4 mb-4">
            <div className="w-full md:w-1/2 md:pr-4 mb-4 md:mb-0 md:border-r md:border-gray-200">
              <div className="flex gap-4">
                <div>
                  <img
                    src={invoiceData.logo}
                    alt="logo"
                    className="h-32"
                    srcset=""
                  />
                </div>

                <div>
                  <h3 className="font-bold text-base mb-1 text-gray-900">
                    {invoiceData.companyName || "N/A"}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {invoiceData.address?.streetAddress || "N/A"} <br />
                    {invoiceData.address?.city || "N/A"},{" "}
                    {invoiceData.address?.state || "N/A"}{" "}
                    {invoiceData.address?.pincode || ""} <br />
                    {!invoiceData.isGstExempted && invoiceData.gst && (
                      <span className="uppercase">
                        GSTIN: {invoiceData.gst} <br />
                      </span>
                    )}
                    {invoiceData.mobile && (
                      <span>
                        Contact: {invoiceData.mobile} <br />
                      </span>
                    )}
                    {invoiceData.email && (
                      <span>Email: {invoiceData.email}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full md:w-1/2 md:pl-4">
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">
                    Invoice No:
                  </span>
                  <span className="font-bold text-gray-900">
                    {invoiceData.salesData.invoiceNumber || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">
                    Invoice Date:
                  </span>
                  <span className="text-gray-800">
                    {formatDate(invoiceData.salesData.invoiceDate)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Due Date:</span>
                  <span className="text-gray-800">
                    {formatDate(invoiceData.salesData.dueDate)}
                  </span>
                </div>
                {invoiceData.salesData.poNumber && (
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-700">
                      P.O. Number:
                    </span>
                    <span className="text-gray-800">
                      {invoiceData.salesData.poNumber}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Billing & Shipping Addresses */}
          <div className="flex flex-col md:flex-row border-b border-gray-200 pb-4 mb-4">
            <div className="w-full md:w-1/2 md:pr-4 mb-4 md:mb-0">
              <h4 className="font-semibold text-sm mb-1 text-gray-800">
                Bill To:
              </h4>
              <p className="font-bold text-sm mb-1 text-gray-900">
                {invoiceData.salesData.partyData?.billingAddress?.name || "N/A"}
              </p>
              <p className="text-xs text-gray-600 leading-relaxed">
                {invoiceData.salesData.partyData?.billingAddress
                  ?.streetAddress || "N/A"}{" "}
                <br />
                {invoiceData.salesData.partyData?.billingAddress?.city || "N/A"}
                ,{" "}
                {invoiceData.salesData.partyData?.billingAddress?.state ||
                  "N/A"}{" "}
                {invoiceData.salesData.partyData?.billingAddress?.pincode || ""}{" "}
                <br />
                {/* State Code:{" "}
                {invoiceData.salesData.partyData?.billingAddress?.stateCode ||
                  "N/A"}{" "}
                <br /> */}
                {!invoiceData.isGstExempted &&
                  invoiceData.salesData.partyData?.billingAddress?.gstNo && (
                    <span className="uppercase">
                      GSTIN:{" "}
                      {invoiceData.salesData.partyData.billingAddress.gstNo}{" "}
                      <br />
                    </span>
                  )}
                {invoiceData.salesData.partyData?.panNumber && (
                  <span>
                    PAN: {invoiceData.salesData.partyData.panNumber} <br />
                  </span>
                )}
                {invoiceData.salesData.partyData?.mobile && (
                  <span>Contact: {invoiceData.salesData.partyData.mobile}</span>
                )}
              </p>
            </div>
            <div className="w-full md:w-1/2 md:pl-4 md:border-l md:border-gray-200">
              <h4 className="font-semibold text-sm mb-1 text-gray-800">
                Ship To:
              </h4>
              <p className="font-bold text-sm mb-1 text-gray-900">
                {invoiceData.salesData.partyData?.shippingAddress?.name ||
                  "N/A"}
              </p>
              <p className="text-xs text-gray-600 leading-relaxed">
                {invoiceData.salesData.partyData?.shippingAddress
                  ?.streetAddress || "N/A"}{" "}
                <br />
                {invoiceData.salesData.partyData?.shippingAddress?.city ||
                  "N/A"}
                ,{" "}
                {invoiceData.salesData.partyData?.shippingAddress?.state ||
                  "N/A"}{" "}
                {invoiceData.salesData.partyData?.shippingAddress?.pincode ||
                  ""}{" "}
                <br />
                {/* State Code:{" "}
                {invoiceData.salesData.partyData?.shippingAddress?.stateCode ||
                  "N/A"}{" "}
                <br /> */}
                {!invoiceData.isGstExempted &&
                  invoiceData.salesData.partyData?.shippingAddress?.gstNo && (
                    <span className="uppercase">
                      GSTIN:{" "}
                      {invoiceData.salesData.partyData.shippingAddress.gstNo}{" "}
                      <br />
                    </span>
                  )}
                {invoiceData.salesData.partyData?.phoneNumber && (
                  <span>
                    Contact: {invoiceData.salesData.partyData.phoneNumber}
                  </span>
                )}
              </p>
            </div>
          </div>
          {/* Items Table */}
          <div className="mb-4 overflow-x-auto">
            <table className="w-full min-w-[640px] text-xs border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    #
                  </th>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    Item & Description
                  </th>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    Qty
                  </th>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    HSN/SCN
                  </th>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    Rate (Rs)
                  </th>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    Taxable (Rs)
                  </th>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    Tax %
                  </th>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    Tax Amt (Rs)
                  </th>
                  <th className="border border-gray-300 px-2 py-1.5 text-center">
                    Total (Rs)
                  </th>
                </tr>
              </thead>
              <tbody>
                {(invoiceData.salesData.items || []).map((item, index) => {
                  /* ... Render table rows ... */
                  const itemAmount = parseFloat(item.itemAmount) || 0;
                  const taxAmount = parseFloat(item.taxAmount) || 0;
                  const totalAmountItem = parseFloat(item.totalAmount) || 0;
                  const rate = parseFloat(item.price) || 0;
                  const discountVal = parseFloat(item.discount) || 0;
                  const quantity = parseFloat(item.quantity) || 0;
                  return (
                    <tr
                      key={`preview-item-${index}`}
                      className="odd:bg-white even:bg-gray-50"
                    >
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top">
                        {index + 1}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top">
                        <span className="font-semibold text-gray-800">
                          {item.itemName || "-"}{" "}
                        </span>
                        {item.description && (
                          <p className="text-gray-500 text-[10px] mt-0.5">
                            {item.description}
                          </p>
                        )}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top">
                        {quantity} {item.unit || "PCS"}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top">
                        {item.hsnCode}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top">
                        {rate.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top">
                        {itemAmount.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top">
                        {item.taxRate || 0}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top">
                        {taxAmount.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center align-top font-semibold">
                        {totalAmountItem.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Terms, Totals & Amount in Words */}
          <div className="flex flex-col md:flex-row border-t border-gray-200 pt-4">
            <div className="w-full md:w-1/2 md:pr-4 mb-4 md:mb-0">
              <div className="mb-3">
                <h5 className="font-semibold text-xs mb-1 text-gray-800">
                  Terms and Conditions:
                </h5>
                <p className="text-xs text-gray-600 whitespace-pre-line">
                  {invoiceData.salesData.terms || "Default Terms..."}
                </p>
              </div>
              <div className="mb-3">
                <h5 className="font-semibold text-xs mb-1 text-gray-800">
                  Notes:
                </h5>
                <p className="text-xs text-gray-600">
                  {invoiceData.salesData.notes || "-"}
                </p>
              </div>
            </div>
            <div className="w-full md:w-1/2 md:pl-4">
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">MRP Total:</span>
                  <span className="text-gray-800">
                    Rs {previewTotalMrp.toFixed(2)}
                  </span>
                </div>
                {previewTotalDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount:</span>
                    <span className="text-gray-800">
                      (-) Rs {previewTotalDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between border-t border-dashed border-gray-300 pt-1 mt-1">
                  <span className="text-gray-600">Taxable Amount:</span>
                  <span className="font-medium text-gray-800">
                    Rs {previewItemsTotal.toFixed(2)}
                  </span>
                </div>
                {Object.entries(taxDetailsCalculated.igst).map(
                  ([rate, amount]) =>
                    amount > 0 ? (
                      <div
                        key={`preview-igst-${rate}`}
                        className="flex justify-between"
                      >
                        <span className="text-gray-600">IGST @{rate}%:</span>
                        <span className="text-gray-800">
                          Rs {amount.toFixed(2)}
                        </span>
                      </div>
                    ) : null
                )}
                {Object.entries(taxDetailsCalculated.cgst).map(
                  ([rate, amount]) =>
                    amount > 0 ? (
                      <div
                        key={`preview-cgst-${rate}`}
                        className="flex justify-between"
                      >
                        <span className="text-gray-600">CGST @{rate}%:</span>
                        <span className="text-gray-800">
                          Rs {amount.toFixed(2)}
                        </span>
                      </div>
                    ) : null
                )}
                {Object.entries(taxDetailsCalculated.sgst).map(
                  ([rate, amount]) =>
                    amount > 0 ? (
                      <div
                        key={`preview-sgst-${rate}`}
                        className="flex justify-between"
                      >
                        <span className="text-gray-600">SGST @{rate}%:</span>
                        <span className="text-gray-800">
                          Rs {amount.toFixed(2)}
                        </span>
                      </div>
                    ) : null
                )}
                {taxDetailsCalculated.totalTax > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Tax:</span>
                    <span className="font-medium text-gray-800">
                      Rs {taxDetailsCalculated.totalTax.toFixed(2)}
                    </span>
                  </div>
                )}
                {Math.abs(previewRoundOffValue) > 0.005 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Round Off:</span>
                    <span className="text-gray-800">
                      Rs {previewRoundOffValue.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-300 pt-2 mt-2">
                  <span className="font-bold text-sm text-gray-900">
                    Grand Total:
                  </span>
                  <span className="font-bold text-sm text-gray-900">
                    Rs {previewTotalAmountRoundoff.toFixed(2)}
                  </span>
                </div>
                <div className="font-medium text-xs text-right text-gray-800">
                  {numberToWords(previewTotalAmountRoundoff)}
                </div>
              </div>
            </div>
          </div>
          {/* Footer: Bank Details & Signature */}
          <div className="flex flex-col md:flex-row border-t border-gray-200 pt-4 mt-4">
            <div className="w-full md:w-1/2 md:pr-4 mb-4 md:mb-0">
              <h5 className="font-semibold text-xs mb-1 text-gray-800">
                Bank Details:
              </h5>
              <p className="text-xs text-gray-600 leading-relaxed">
                Bank Name: {invoiceData.bankDetails?.bankName || "-"} <br />
                A/C Name: {invoiceData.bankDetails?.accountName || "-"} <br />
                A/C No: {invoiceData.bankDetails?.accountNumber || "-"} <br />
                IFSC: {invoiceData.bankDetails?.ifsc || "-"}
              </p>
            </div>
            <div className="w-full md:w-1/2 md:pl-4 flex flex-col items-end justify-between min-h-[80px]">
              <p className="text-xs font-semibold text-gray-700 self-end">
                For {invoiceData.companyName || "Your Company"}
              </p>
              <div className="w-48 text-center mt-auto">
                {" "}
                <img src={invoiceData.signature} alt="signature" srcset="" />
                <p className="text-xs text-gray-600 border-t border-gray-400 pt-1">
                  Authorised Signatory
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
